import React, {useEffect, useState} from 'react';
import { Modal, Tabs, Table, Typography, Button, message } from 'antd';
import {
    CloseOutlined, WarningOutlined, CheckCircleOutlined,
    CloseCircleOutlined, MedicineBoxOutlined, FileTextOutlined,
    CheckOutlined, FilePdfOutlined, LoadingOutlined, FolderOpenOutlined
} from '@ant-design/icons';
import ApiClient from '../api';
import AudiDoxLogo from '../assets/audidox_lighttheme.jpeg';
import {DocumentViewerModal} from "./DocumentViewerModal";

const api_client = new ApiClient();
const { Text, Title } = Typography;

/* ── Helpers ──────────────────────────────────────────────── */
const fmt    = (n, currency = 'MUR') => `${currency} ${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
const fmtNum = (n) => Number(n).toLocaleString('en-US', { minimumFractionDigits: 2 });
// If a DISALLOWED item still has a partial refund (e.g. scale-of-costs capped
// visit fees), show the actual deducted amount (amount - refunded) instead of
// the full claimed amount.
const getDeductedAmount = (item) => {
    const refunded = item.refunded || 0;
    if (item.status === 'DISALLOWED' && refunded > 0) {
        return item.amount - refunded;
    }
    return item.amount;
};
/* ── PDF Export ───────────────────────────────────────────── */
const exportReceiptPDF = async (assessmentData, caseData) => {
    const { jsPDF }   = await import('jspdf');
    const autoTable   = (await import('jspdf-autotable')).default;

    const currency = assessmentData.currency || 'MUR';

    const doc    = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const W      = 210;
    const MARGIN = 18;

    // ── Light theme palette ──────────────────────────────────
    const TEAL        = [ 14,  99,  96];
    const TEAL_LIGHT  = [220, 237, 237];
    const RED         = [180,  30,  30];
    const RED_LIGHT   = [253, 235, 235];
    const BLACK       = [ 15,  15,  15];
    const DARK_GRAY   = [ 60,  70,  70];
    const MID_GRAY    = [120, 130, 130];
    const LIGHT_GRAY  = [220, 225, 225];
    const OFF_WHITE   = [248, 250, 250];
    const WHITE       = [255, 255, 255];

    const disallowedItems = [
        ...(assessmentData.line_items     || []),
        ...(assessmentData.medicine_items || []),
    ].filter(i => i.status === 'DISALLOWED');

    // ── Header band ──────────────────────────────────────────
    doc.setFillColor(...WHITE);
    doc.rect(0, 0, W, 42, 'F');

    doc.setDrawColor(...LIGHT_GRAY);
    doc.setLineWidth(0.4);
    doc.line(0, 42, W, 42);

    const logoWidth  = 52;
    const logoHeight = (270 / 870) * logoWidth;

    if (AudiDoxLogo) {
        doc.addImage(AudiDoxLogo, 'JPEG', MARGIN, 12, logoWidth, logoHeight);
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...BLACK);
    doc.text('Receipt of Disallowed', W - MARGIN, 16, { align: 'right' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...MID_GRAY);
    doc.text(`Generated: ${new Date().toLocaleString()}`, W - MARGIN, 22, { align: 'right' });
    doc.text(`Invoice: ${assessmentData.invoice_number}`,  W - MARGIN, 28, { align: 'right' });

    let y = 52;

    // ── Patient info block ───────────────────────────────────
    doc.setFillColor(...OFF_WHITE);
    doc.setDrawColor(...LIGHT_GRAY);
    doc.setLineWidth(0.4);
    doc.roundedRect(MARGIN, y, W - MARGIN * 2, 30, 2, 2, 'FD');

    const col1 = MARGIN + 6;
    const col2 = W / 2 + 4;

    const infoLabel = (text, x, yy) => {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.5);
        doc.setTextColor(...MID_GRAY);
        doc.text(text.toUpperCase(), x, yy);
    };
    const infoValue = (text, x, yy, color = BLACK) => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(...color);
        doc.text(String(text), x, yy);
    };

    infoLabel('Patient Name',      col1, y + 8);  infoValue(assessmentData.patient_name,      col1, y + 14);
    infoLabel('IP Number',         col1, y + 20); infoValue(caseData?.ip_no || '—',            col1, y + 26, TEAL);
    infoLabel('Date of Admission', col2, y + 8);  infoValue(assessmentData.date_and_time_of_admission,  col2, y + 14);
    infoLabel('Date of Discharge', col2, y + 20); infoValue(assessmentData.date_and_time_of_discharge,  col2, y + 26);

    y += 38;

    // ── Diagnosis ────────────────────────────────────────────
    infoLabel('Diagnosis', MARGIN, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...DARK_GRAY);
    doc.text(
        (assessmentData.diagnosis || []).join('  ·  ') + `  (ICD-10: ${assessmentData.icd_10})`,
        MARGIN, y
    );
    y += 10;

    // ── Financial summary boxes ──────────────────────────────
    const boxW = (W - MARGIN * 2 - 8) / 3;
    const boxes = [
        { label: 'Total Bill',       value: fmtNum(assessmentData.total_bill_mur),       textColor: BLACK,     borderColor: LIGHT_GRAY,  bgColor: OFF_WHITE  },
        { label: 'Total Approved',   value: fmtNum(assessmentData.total_approved_mur),   textColor: TEAL,      borderColor: TEAL,        bgColor: TEAL_LIGHT },
        { label: 'Total Disallowed', value: fmtNum(assessmentData.total_disallowed_mur), textColor: RED,       borderColor: RED,         bgColor: RED_LIGHT  },
    ];

    boxes.forEach((b, i) => {
        const bx = MARGIN + i * (boxW + 4);
        doc.setFillColor(...b.bgColor);
        doc.setDrawColor(...b.borderColor);
        doc.setLineWidth(0.5);
        doc.roundedRect(bx, y, boxW, 22, 2, 2, 'FD');
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.5);
        doc.setTextColor(...MID_GRAY);
        doc.text(b.label.toUpperCase(), bx + 5, y + 8);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(...b.textColor);
        doc.text(`${currency} ${b.value}`, bx + 5, y + 17);
    });

    y += 30;

    // ── Section header ───────────────────────────────────────
    doc.setFillColor(...RED);
    doc.rect(MARGIN, y, 3, 6, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...RED);
    doc.text('DISALLOWED ITEMS', MARGIN + 6, y + 5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...MID_GRAY);
    doc.text(
        `${disallowedItems.length} items  ·  ${((assessmentData.total_disallowed_mur / assessmentData.total_bill_mur) * 100).toFixed(1)}% of total bill`,
        W - MARGIN, y + 5, { align: 'right' }
    );
    y += 10;

    // ── Disallowed items table ───────────────────────────────
    autoTable(doc, {
        startY: y,
        margin: { left: MARGIN, right: MARGIN },
        head: [['Description', 'Group', 'Qty', 'Unit Price', 'Disallowed', 'Reason']],
        // body: disallowedItems.map(item => [
        //     item.description,
        //     item.service_group,
        //     item.quantity,
        //     `${currency} ${fmtNum(item.unit_price)}`,
        //     `${currency} ${fmtNum(getDeductedAmount(item))}`,
        //     item.remarks || '—',
        // ]),
        body: disallowedItems.map(item => [ 
            item.description,
            item.service_group,
            item.quantity,
            isDoctorFeeItem(item) ? '—' : `${currency} ${fmtNum(item.unit_price)}`,
            `${currency} ${fmtNum(getDeductedAmount(item))}`,
            item.remarks || '—',
        ]),
        headStyles: {
            fillColor: TEAL,
            textColor: WHITE,
            fontSize: 7,
            fontStyle: 'bold',
            halign: 'left',
        },
        bodyStyles: {
            fillColor: WHITE,
            textColor: BLACK,
            fontSize: 7.5,
            lineColor: LIGHT_GRAY,
            lineWidth: 0.2,
        },
        alternateRowStyles: {
            fillColor: OFF_WHITE,
        },
        columnStyles: {
            0: { cellWidth: 44 },
            1: { cellWidth: 24 },
            2: { cellWidth: 10, halign: 'center' },
            3: { cellWidth: 24, halign: 'right' },
            4: { cellWidth: 24, halign: 'right', textColor: RED, fontStyle: 'bold' },
            5: { cellWidth: 'auto', textColor: DARK_GRAY, fontStyle: 'italic' },
        },
    });

    // ── Page footer ──────────────────────────────────────────
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        const pageH = doc.internal.pageSize.height;

        doc.setDrawColor(...LIGHT_GRAY);
        doc.setLineWidth(0.3);
        doc.line(MARGIN, pageH - 12, W - MARGIN, pageH - 12);

        doc.setFillColor(...TEAL);
        doc.rect(0, pageH - 10, 4, 10, 'F');

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.5);
        doc.setTextColor(...MID_GRAY);
        doc.text('AudiDox · Adjudication Platform · Confidential', MARGIN, pageH - 4);
        doc.text(`Page ${i} of ${pageCount}`, W - MARGIN, pageH - 4, { align: 'right' });
    }

    doc.save(`AudiDox_Disallowed_Items_${assessmentData.invoice_number}_${assessmentData.patient_name.replace(/\s+/g, '_')}.pdf`);
};

/* ── Status pill ──────────────────────────────────────────── */
const StatusPill = ({ status }) => {
    const isDisallowed = status === 'DISALLOWED';
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '2px 10px', borderRadius: 5, fontSize: 10,
            fontFamily: 'monospace', fontWeight: 700, letterSpacing: '0.6px',
            background: isDisallowed ? 'rgba(255,77,79,0.1)' : 'rgba(121,167,165,0.1)',
            color:      isDisallowed ? '#ff4d4f'             : '#79A7A5',
            border: `1px solid ${isDisallowed ? 'rgba(255,77,79,0.25)' : 'rgba(121,167,165,0.25)'}`,
        }}>
            {isDisallowed ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
            {status}
        </span>
    );
};

const AmountCell = ({ amount, status, currency = 'MUR' }) => (
    <Text style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600, color: status === 'DISALLOWED' ? '#ff4d4f' : '#79A7A5' }}>
        {fmt(amount, currency)}
    </Text>
);

/* ── Summary bar ──────────────────────────────────────────── */
const SummaryBar = ({ data }) => {
    const currency = data.currency || 'MUR';
    const items = [
        { label: 'Total Bill',       value: fmt(data.total_bill_mur, currency),        color: '#F0F8F7' },
        { label: 'Total Approved',   value: fmt(data.total_approved_mur, currency),    color: '#79A7A5' },
        { label: 'Total Disallowed', value: fmt(data.total_disallowed_mur, currency),  color: '#ff4d4f' },
        { label: 'Patient',          value: data.patient_name,                         color: '#9DBDBB' },
        { label: 'Invoice',          value: data.invoice_number,                       color: '#9DBDBB', mono: true },
        { label: 'Admission',        value: data.date_and_time_of_admission,           color: '#6B8585' },
        { label: 'Discharge',        value: data.date_and_time_of_discharge,           color: '#6B8585' },
    ];
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', background: '#0D1515', borderBottom: '1px solid rgba(121,167,165,0.1)' }}>
            {items.map((item, i) => (
                <div key={i} style={{ padding: '12px 20px', borderRight: '1px solid rgba(121,167,165,0.08)', minWidth: 130 }}>
                    <Text style={{ color: '#3D5454', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.8px', display: 'block', marginBottom: 3 }}>{item.label}</Text>
                    <Text style={{ color: item.color, fontSize: 12, fontFamily: item.mono ? 'monospace' : 'inherit', fontWeight: 600 }}>{item.value}</Text>
                </div>
            ))}
            <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {(data.diagnosis || []).map((d, i) => (
                    <span key={i} style={{ background: 'rgba(121,167,165,0.08)', border: '1px solid rgba(121,167,165,0.2)', borderRadius: 4, padding: '2px 8px', color: '#79A7A5', fontSize: 11 }}>{d}</span>
                ))}
                <span style={{ background: 'rgba(121,167,165,0.04)', border: '1px solid rgba(121,167,165,0.12)', borderRadius: 4, padding: '2px 8px', color: '#3D5454', fontSize: 11, fontFamily: 'monospace' }}>{data.icd_10}</span>
            </div>
        </div>
    );
};
// Doctor fee items are billed per-visit, not per-unit — showing "unit price"
// alongside claimed amount is misleading, so we blank it for these rows.
const isDoctorFeeItem = (item) => {
    const desc = (item.description || '').trim().toUpperCase();
    const group = (item.service_group || '').toUpperCase();
    return desc.startsWith('DR.') || desc.startsWith('DR ') ||
           ['CONSULTATION', 'INPATIENT VISIT'].includes(group);
};
/* ── Table columns ────────────────────────────────────────── */
const buildColumns = (showRemarks = true, currency = 'MUR') => [
    { title: 'Description', dataIndex: 'description', key: 'description', width: '28%',
        render: (v, row) => <Text style={{ color: row.status === 'DISALLOWED' ? '#ff6b6b' : '#F0F8F7', fontSize: 12 }}>{v}</Text> },

    { title: 'Qty', dataIndex: 'quantity', key: 'quantity', width: '5%', align: 'center',
        render: (v, row) => <Text style={{ color: row.status === 'DISALLOWED' ? 'rgba(255,77,79,0.7)' : '#6B8585', fontSize: 12, fontFamily: 'monospace' }}>{v}</Text> },
    // { title: 'Unit Price', dataIndex: 'unit_price', key: 'unit_price', width: '12%', align: 'right',
    //     render: (v, row) => <Text style={{ color: row.status === 'DISALLOWED' ? 'rgba(255,77,79,0.7)' : '#6B8585', fontSize: 11, fontFamily: 'monospace' }}>{fmt(v, currency)}</Text> },
    { title: 'Unit Price', dataIndex: 'unit_price', key: 'unit_price', width: '12%', align: 'right',
    render: (v, row) => isDoctorFeeItem(row)
        ? <Text style={{ color: '#2A3A3A', fontSize: 11 }}>—</Text>
        : <Text style={{ color: row.status === 'DISALLOWED' ? 'rgba(255,77,79,0.7)' : '#6B8585', fontSize: 11, fontFamily: 'monospace' }}>{fmt(v, currency)}</Text> },
    // { title: 'Claimed', dataIndex: 'amount', key: 'amount', width: '12%', align: 'right',
    //     render: (v, row) => <AmountCell amount={getDeductedAmount(row)} status={row.status} currency={currency} /> },
    { title: 'Claimed', dataIndex: 'amount', key: 'amount', width: '12%', align: 'right',
    render: (v, row) => <AmountCell amount={row.amount} status={row.status} currency={currency} /> },
    { title: 'Refunded', dataIndex: 'refunded', key: 'refunded', width: '12%', align: 'right',
        render: (v, row) => <AmountCell amount={v} status={row.status} currency={currency} /> },
    { title: 'Status', dataIndex: 'status', key: 'status', width: '10%',
        render: (v) => <StatusPill status={v} /> },
    ...(showRemarks ? [{
        title: 'Remarks', dataIndex: 'remarks', key: 'remarks',
        render: (v, row) => v
            ? <Text style={{ color: row.status === 'DISALLOWED' ? 'rgba(255,77,79,0.8)' : '#6B8585', fontSize: 11, fontStyle: 'italic' }}>{v}</Text>
            : <Text style={{ color: '#2A3A3A', fontSize: 11 }}>—</Text>,
    }] : []),
];

/* ── Section table ────────────────────────────────────────── */
const SectionTable = ({ title, items, currency = 'MUR' }) => {
    if (!items.length) return null;
    const groupIcons = {
        'LABORATORY': '🧪', 'DRUGS': '💊', 'IMAGING': '🩻', 'SERVICES': '🏥',
        'CONSUMABLES': '📦', 'ENDOSCOPY UNIT': '🔬', 'CARDIAC UNIT': '❤️',
        'EMERGENCY': '🚨', 'CONSULTATION': '👨‍⚕️', 'INPATIENT VISIT': '🛏️',
    };
    return (
        <div style={{ marginBottom: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 13 }}>{groupIcons[title] || '📋'}</span>
                <Text style={{ color: '#9DBDBB', fontSize: 11, fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600 }}>{title}</Text>
                <span style={{ background: 'rgba(121,167,165,0.08)', border: '1px solid rgba(121,167,165,0.15)', borderRadius: 4, padding: '1px 7px', color: '#3D5454', fontSize: 10, fontFamily: 'monospace' }}>{items.length}</span>
            </div>
            <Table dataSource={items} columns={buildColumns(true, currency)} rowKey={(r, i) => `${r.description}-${i}`}
                   pagination={false} size="small" style={{ background: 'transparent' }}
                   rowClassName={(r) => r.status === 'DISALLOWED' ? 'row-disallowed' : 'row-approved'} />
        </div>
    );
};

/* ── Doc Check Strip ──────────────────────────────────────── */
const DOC_FLAGS = [
    { key: 'invoice_detected',                            label: 'Invoice'           },
    { key: 'diagnosis_covered',                           label: 'Diagnosis Covered' },
    { key: 'guarantee_of_payment_detected',               label: 'GOP'               },
    { key: 'discharge_summary_or_medicalreport_detected', label: 'Discharge Summary' },
];

const DocCheckStrip = ({ data }) => {
    const allOk = DOC_FLAGS.every(f => data[f.key] === true);

    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
            padding: '8px 20px',
            background: allOk ? 'rgba(121,167,165,0.04)' : 'rgba(255,77,79,0.04)',
            borderBottom: `1px solid ${allOk ? 'rgba(121,167,165,0.12)' : 'rgba(255,77,79,0.15)'}`,
        }}>
            <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '3px 10px', borderRadius: 5, fontSize: 10,
                fontFamily: 'monospace', fontWeight: 700, letterSpacing: '0.7px',
                background: allOk ? 'rgba(121,167,165,0.12)' : 'rgba(255,77,79,0.12)',
                color:      allOk ? '#79A7A5'               : '#ff4d4f',
                border:     `1px solid ${allOk ? 'rgba(121,167,165,0.3)' : 'rgba(255,77,79,0.3)'}`,
                marginRight: 6,
            }}>
                {allOk ? <CheckCircleOutlined /> : <WarningOutlined />}
                {allOk ? 'DOC CHECK GOOD' : 'DOC CHECK FAILED'}
            </span>

            {DOC_FLAGS.map(({ key, label }) => {
                const ok = data[key] === true;
                return (
                    <span key={key} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '2px 9px', borderRadius: 5, fontSize: 10,
                        fontFamily: 'monospace', letterSpacing: '0.5px', fontWeight: 600,
                        background: ok ? 'rgba(121,167,165,0.07)' : 'rgba(255,77,79,0.08)',
                        color:      ok ? '#79A7A5'                 : '#ff4d4f',
                        border:     `1px solid ${ok ? 'rgba(121,167,165,0.2)' : 'rgba(255,77,79,0.25)'}`,
                    }}>
                        {ok ? <CheckCircleOutlined style={{ fontSize: 10 }} /> : <CloseCircleOutlined style={{ fontSize: 10 }} />}
                        {label}
                    </span>
                );
            })}
        </div>
    );
};

/* ── Main Modal ───────────────────────────────────────────── */
const AssessmentModal = ({ open, onClose, caseData, onMarkedRead, showMarkAsReadButton }) => {
    const [activeTab,   setActiveTab]   = useState('disallowed');
    const [markingRead, setMarkingRead] = useState(false);
    const [exporting,   setExporting]   = useState(false);
    const [markedRead,  setMarkedRead]  = useState(false);
    const [docsOpen,    setDocsOpen]    = useState(false);

    useEffect(() => {
        setMarkedRead(false);
        setActiveTab('disallowed');
    }, [caseData?.stack_id]);

    const assessmentData = caseData?.assessment_data;
    console.log('assessmentData', assessmentData);
    if (!assessmentData) return null;

    const currency = assessmentData.currency || 'MUR';

    const allItems = [
        ...(assessmentData.line_items     || []),
        ...(assessmentData.medicine_items || []),
    ];
    const disallowedItems = allItems.filter(i => i.status === 'DISALLOWED');
    const grouped = allItems.reduce((acc, item) => {
        const g = item.service_group || 'OTHER';
        if (!acc[g]) acc[g] = [];
        acc[g].push(item);
        return acc;
    }, {});

    const handleMarkRead = async () => {
        if (markedRead || !caseData?.stack_id) return;
        setMarkingRead(true);
        try {
            let user_email = localStorage.getItem('email');
            await api_client.markasreadpro(caseData.stack_id, user_email);
            setMarkedRead(true);
            message.success('Case marked as read.');
            if (onMarkedRead) onMarkedRead(caseData.stack_id);
        } catch {
            message.error('Failed to mark as read.');
        } finally {
            setMarkingRead(false);
        }
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            await exportReceiptPDF(assessmentData, caseData);
            message.success('Receipt exported successfully.');
        } catch (e) {
            console.error(e);
            message.error('Export failed.');
        } finally {
            setExporting(false);
        }
    };

    const tabItems = [
        {
            key: 'disallowed',
            label: (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <WarningOutlined style={{ color: '#ff4d4f' }} />
                    Non-Payables
                    <span style={{ background: 'rgba(255,77,79,0.15)', color: '#ff4d4f', border: '1px solid rgba(255,77,79,0.3)', borderRadius: 4, padding: '0px 6px', fontSize: 10, fontFamily: 'monospace', fontWeight: 700 }}>
                        {disallowedItems.length}
                    </span>
                </span>
            ),
            children: (
                <div style={{ padding: '20px 0' }}>
                    <div style={{ background: 'rgba(255,77,79,0.04)', border: '1px solid rgba(255,77,79,0.15)', borderRadius: 8, padding: '14px 20px', marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                            <Text style={{ color: '#ff4d4f', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: 'monospace', fontWeight: 700, display: 'block' }}>Total Disallowed Amount</Text>
                            <Text style={{ color: '#ff4d4f', fontSize: 22, fontWeight: 700, fontFamily: 'monospace' }}>{fmt(assessmentData.total_disallowed_mur, currency)}</Text>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <Text style={{ color: '#3D5454', fontSize: 11, display: 'block', marginBottom: 2 }}>
                                {disallowedItems.length} items flagged across {[...new Set(disallowedItems.map(i => i.service_group))].length} service groups
                            </Text>
                            <Text style={{ color: '#3D5454', fontSize: 11 }}>
                                {((assessmentData.total_disallowed_mur / assessmentData.total_bill_mur) * 100).toFixed(1)}% of total bill
                            </Text>
                        </div>
                    </div>
                    <Table dataSource={disallowedItems} columns={buildColumns(true, currency)} rowKey={(r, i) => `disallowed-${i}`}
                           pagination={false} size="small" style={{ background: 'transparent' }} rowClassName={() => 'row-disallowed'} />
                </div>
            ),
        },
        {
            key: 'full',
            label: (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FileTextOutlined style={{ color: '#79A7A5' }} />
                    Full Assessment
                    <span style={{ background: 'rgba(121,167,165,0.1)', color: '#79A7A5', border: '1px solid rgba(121,167,165,0.2)', borderRadius: 4, padding: '0px 6px', fontSize: 10, fontFamily: 'monospace', fontWeight: 700 }}>
                        {allItems.length}
                    </span>
                </span>
            ),
            children: (
                <div style={{ padding: '20px 0' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 28 }}>
                        {[
                            { label: 'Total Bill',  value: fmt(assessmentData.total_bill_mur, currency),       color: '#F0F8F7', border: 'rgba(240,248,247,0.1)' },
                            { label: 'Approved',    value: fmt(assessmentData.total_approved_mur, currency),   color: '#79A7A5', border: 'rgba(121,167,165,0.2)'  },
                            { label: 'Disallowed',  value: fmt(assessmentData.total_disallowed_mur, currency), color: '#ff4d4f', border: 'rgba(255,77,79,0.2)'    },
                        ].map((s, i) => (
                            <div key={i} style={{ background: '#0D1515', border: `1px solid ${s.border}`, borderRadius: 8, padding: '14px 18px' }}>
                                <Text style={{ color: '#3D5454', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: 'monospace', display: 'block', marginBottom: 4 }}>{s.label}</Text>
                                <Text style={{ color: s.color, fontSize: 18, fontWeight: 700, fontFamily: 'monospace' }}>{s.value}</Text>
                            </div>
                        ))}
                    </div>
                    {Object.entries(grouped).map(([group, items]) => (
                        <SectionTable key={group} title={group} items={items} currency={currency} />
                    ))}
                </div>
            ),
        },
    ];

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width="92vw"
            centered
            closeIcon={<CloseOutlined style={{ color: '#6B8585' }} />}
            styles={{
                content: { background: '#0D1515', border: '1px solid rgba(121,167,165,0.15)', borderRadius: 12, padding: 0, overflow: 'hidden' },
                header:  { background: '#0D1515', borderBottom: '1px solid rgba(121,167,165,0.1)', padding: '14px 24px', margin: 0 },
                mask:    { backdropFilter: 'blur(6px)', background: 'rgba(8,10,12,0.85)' },
                body:    { padding: 0, maxHeight: '88vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
            }}
            title={
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 32 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <MedicineBoxOutlined style={{ color: '#79A7A5', fontSize: 18 }} />
                        <div>
                            <Title level={5} style={{ color: '#F0F8F7', margin: 0, fontFamily: 'Space Grotesk', letterSpacing: '-0.3px' }}>
                                Adjudication Report
                            </Title>
                            <Text style={{ color: '#3D5454', fontSize: 11, fontFamily: 'monospace' }}>
                                {assessmentData.invoice_number} · {assessmentData.patient_name}
                            </Text>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 10 }}>
                        {showMarkAsReadButton && (
                            <Button
                                size="middle"
                                icon={markingRead ? <LoadingOutlined /> : <CheckOutlined />}
                                onClick={handleMarkRead}
                                disabled={markedRead || markingRead}
                                style={{
                                    background: markedRead ? 'rgba(121,167,165,0.08)' : 'transparent',
                                    border: `1px solid ${markedRead ? 'rgba(121,167,165,0.3)' : 'rgba(121,167,165,0.2)'}`,
                                    color: markedRead ? '#3D5454' : '#79A7A5',
                                    borderRadius: 7, fontSize: 12, height: 34,
                                }}
                            >
                                {markedRead ? 'Marked as Read' : 'Mark as Read'}
                            </Button>
                        )}

                        <Button
                            size="middle"
                            icon={<FolderOpenOutlined />}
                            onClick={() => setDocsOpen(true)}
                            style={{
                                background:   'rgba(121,167,165,0.06)',
                                border:       '1px solid rgba(121,167,165,0.2)',
                                color:        '#79A7A5',
                                borderRadius: 7, fontSize: 12, height: 34,
                            }}
                        >
                            View Documents
                        </Button>

                        <Button
                            size="middle"
                            icon={exporting ? <LoadingOutlined /> : <FilePdfOutlined />}
                            onClick={handleExport}
                            disabled={exporting}
                            style={{
                                background:   'rgba(220,60,60,0.08)',
                                border:       '1px solid rgba(220,60,60,0.25)',
                                color:        '#ff6b6b',
                                borderRadius: 7, fontSize: 12, height: 34,
                            }}
                        >
                            Export Receipt
                        </Button>
                    </div>
                </div>
            }
        >
            <SummaryBar data={assessmentData} />
            <DocCheckStrip data={assessmentData} />

            <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px 24px' }}>
                <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} style={{ marginTop: 4 }} />
            </div>

            <style>{`
                .ant-table { background: transparent !important; }
                .ant-table-thead > tr > th { background: #0D1515 !important; color: #3D5454 !important; border-bottom: 1px solid rgba(121,167,165,0.1) !important; font-size: 10px !important; text-transform: uppercase; letter-spacing: 0.8px; font-family: monospace; padding: 8px 12px !important; }
                .ant-table-tbody > tr > td { background: transparent !important; border-bottom: 1px solid rgba(121,167,165,0.05) !important; padding: 8px 12px !important; }
                .ant-table-tbody > tr:last-child > td { border-bottom: none !important; }
                .row-disallowed > td { background: rgba(255,77,79,0.03) !important; }
                .row-disallowed:hover > td { background: rgba(255,77,79,0.07) !important; }
                .row-approved:hover > td { background: rgba(121,167,165,0.04) !important; }
                .ant-tabs-tab { color: #3D5454 !important; font-family: monospace; font-size: 12px; }
                .ant-tabs-tab-active .ant-tabs-tab-btn { color: #F0F8F7 !important; }
                .ant-tabs-ink-bar { background: #79A7A5 !important; }
                .ant-tabs-nav { border-bottom: 1px solid rgba(121,167,165,0.1) !important; margin-bottom: 0 !important; }
                .ant-tabs-nav::before { border-bottom: none !important; }
                ::-webkit-scrollbar { width: 4px; height: 4px; }
                ::-webkit-scrollbar-track { background: transparent; }
                ::-webkit-scrollbar-thumb { background: rgba(121,167,165,0.2); border-radius: 4px; }
            `}</style>

            <DocumentViewerModal
                key={caseData?.stack_id}
                open={docsOpen}
                onClose={() => setDocsOpen(false)}
                fileList={caseData?.file_list || []}
            />
        </Modal>
    );
};

export default AssessmentModal;